import { useEffect, useRef } from 'react';

export type ShortcutAction = 'bringToFront' | 'bringToBack' | 'copy' | 'paste' | 'undo' | 'redo';

// Global registry for registered functions
const actionRegistry = {
    bringToFront: new Set<(componentId: string) => void>(),
    bringToBack: new Set<(componentId: string) => void>(),
    copy: new Set<(componentId: string) => void>(),
    paste: new Set<(componentId: string) => void>(),
    undo: new Set<(componentId: string) => void>(),
    redo: new Set<(componentId: string) => void>(),
};

// Register function for a specific action
export const registerShortcutAction = (action: ShortcutAction, callback: (componentId: string) => void) => {
    actionRegistry[action].add(callback);

    return () => {
        actionRegistry[action].delete(callback);
    };
};

// Unregister function for a specific action
export const unregisterShortcutAction = (action: ShortcutAction, callback: (componentId: string) => void) => {
    actionRegistry[action].delete(callback);
};

// Execute all registered functions for a specific action
const executeAction = (action: ShortcutAction, componentId: string) => {
    actionRegistry[action].forEach((callback) => {
        try {
            callback(componentId);
        } catch (error) {
            console.error(`Error executing ${action} callback:`, error);
        }
    });
};

export const useShortcut = () => {
    const isListenerActive = useRef(false);
    const selectedComponentId = useRef<string>('');

    // get the selected component id
    useEffect(() => {
        const handleIsSelectedComponent = (event: Event) => {
            const customEvent = event as CustomEvent;
            selectedComponentId.current = customEvent.detail.componentId;
        };

        document.addEventListener('isSelectedComponent', handleIsSelectedComponent);

        return () => {
            document.removeEventListener('isSelectedComponent', handleIsSelectedComponent);
        };
    }, []);

    useEffect(() => {
        // Prevent multiple listeners from being added
        if (isListenerActive.current) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const key = event.key?.toLowerCase?.() ?? '';
            const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

            // Check if focus is on a text input element
            const activeElement = document.activeElement;
            const isTextInput =
                activeElement &&
                (activeElement.tagName === 'INPUT' ||
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.getAttribute('contenteditable') === 'true' ||
                    activeElement.getAttribute('role') === 'textbox');

            // Copy: Cmd+C on macOS, Ctrl+C on others
            if (key === 'c' && ((isMac && event.metaKey) || (!isMac && event.ctrlKey))) {
                // Don't prevent default if focus is on a text input - allow system copy
                if (isTextInput) {
                    return;
                }
                event.preventDefault();
                executeAction('copy', selectedComponentId.current);
                return;
            } else if (key === 'v' && ((isMac && event.metaKey) || (!isMac && event.ctrlKey))) {
                // Don't prevent default if focus is on a text input - allow system paste
                if (isTextInput) {
                    return;
                }
                event.preventDefault();
                executeAction('paste', selectedComponentId.current);
            } else if (event.shiftKey && key === 'z' && ((isMac && event.metaKey) || (!isMac && event.ctrlKey))) {
                event.preventDefault();
                executeAction('redo', selectedComponentId.current);
            } else if (key === 'z' && ((isMac && event.metaKey) || (!isMac && event.ctrlKey))) {
                event.preventDefault();
                executeAction('undo', selectedComponentId.current);
            } else if (event.ctrlKey && key === ']') {
                event.preventDefault();
                executeAction('bringToFront', selectedComponentId.current);
            } else if (event.ctrlKey && key === '[') {
                event.preventDefault();
                executeAction('bringToBack', selectedComponentId.current);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        isListenerActive.current = true;

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            isListenerActive.current = false;
        };
    }, []);

    return {
        registerShortcutAction,
        unregisterShortcutAction,
    };
};
